import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useGetUserQuery,
  useUpdateUserRoleMutation,
  useSetUserBanMutation,
  useDeleteUserMutation,
  type AdminUserDetail,
} from '../../redux/users/users.api';
import { useAppSelector } from '../../redux/store';

const ADMIN_ROLE = 3;
const TESTER_ROLE = 2;
const USER_ROLE = 1;

const ROLE_OPTIONS: Array<{ value: number; label: string }> = [
  { value: USER_ROLE, label: 'User' },
  { value: TESTER_ROLE, label: 'Tester' },
  { value: ADMIN_ROLE, label: 'Admin' },
];

const roleLabel = (role: number): string => {
  if (role === ADMIN_ROLE) return 'Admin';
  if (role === TESTER_ROLE) return 'Tester';
  return 'User';
};

const roleClass = (role: number): string => {
  if (role === ADMIN_ROLE) return 'admin-users__role admin-users__role--admin';
  if (role === TESTER_ROLE) return 'admin-users__role admin-users__role--tester';
  return 'admin-users__role';
};

const initial = (u: AdminUserDetail): string => {
  return (u.firstName || u.email || '?').charAt(0).toUpperCase();
};

const formatDate = (s: string | null): string => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const truncateToken = (token: string | null): string => {
  if (!token) return '—';
  if (token.length <= 24) return token;
  return `${token.slice(0, 12)}…${token.slice(-8)}`;
};

interface FieldProps {
  label: string;
  value: React.ReactNode;
}

const Field = ({ label, value }: FieldProps): React.JSX.Element => (
  <div className="user-detail__field">
    <span className="user-detail__field-label">{label}</span>
    <span className="user-detail__field-value">{value || '—'}</span>
  </div>
);

const extractErrorMessage = (err: unknown): string => {
  if (typeof err === 'object' && err !== null) {
    const e = err as { data?: { message?: string }; status?: number | string };
    if (e.data?.message) return e.data.message;
    if (e.status === 'FETCH_ERROR') return 'Network error.';
  }
  return 'Action failed';
};

export const AdminUserDetailPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const currentAdmin = useAppSelector((state) => state.auth.admin);
  const { data: u, isLoading, error } = useGetUserQuery(id ?? '', {
    skip: !id,
  });

  const [updateRole, { isLoading: isRoleSaving }] = useUpdateUserRoleMutation();
  const [setBan, { isLoading: isBanSaving }] = useSetUserBanMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  const [actionError, setActionError] = useState<string | null>(null);

  if (isLoading) {
    return <div className="admin-users__loading">Loading user…</div>;
  }

  if (error || !u) {
    return (
      <div className="user-detail">
        <Link to="/admin/dashboard/users" className="user-detail__back">
          ← Back to users
        </Link>
        <div className="admin-users__error">
          {error && 'status' in error && error.status === 404
            ? 'User not found.'
            : 'Failed to load user.'}
        </div>
      </div>
    );
  }

  const isSelf = currentAdmin?.id === u.id;
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';
  const hasLocation = !!u.location && (u.location.city || u.location.country);

  const handleRoleChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): Promise<void> => {
    const next = Number(e.target.value);
    if (next === u.role) return;
    const label = roleLabel(next);
    if (
      !window.confirm(
        `Change ${fullName}'s role from ${roleLabel(u.role)} to ${label}?`,
      )
    ) {
      e.target.value = String(u.role);
      return;
    }
    setActionError(null);
    try {
      await updateRole({ id: u.id, role: next }).unwrap();
    } catch (err) {
      setActionError(extractErrorMessage(err));
      e.target.value = String(u.role);
    }
  };

  const handleBanToggle = async (): Promise<void> => {
    const next = !u.isBanned;
    const verb = next ? 'Ban' : 'Unban';
    if (!window.confirm(`${verb} ${fullName}?`)) return;
    setActionError(null);
    try {
      await setBan({ id: u.id, isBanned: next }).unwrap();
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (
      !window.confirm(
        `Permanently delete ${fullName}? This removes their account, posts, ` +
          `comments, events, RSVPs, and chat messages. This action cannot be undone.`,
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      await deleteUser(u.id).unwrap();
      navigate('/admin/dashboard/users', { replace: true });
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  };

  return (
    <div className="user-detail">
      <Link to="/admin/dashboard/users" className="user-detail__back">
        ← Back to users
      </Link>

      <div className="user-detail__header">
        {u.avatarUrl ? (
          <img src={u.avatarUrl} alt="" className="user-detail__avatar" />
        ) : (
          <div className="user-detail__avatar user-detail__avatar--blank">
            {initial(u)}
          </div>
        )}
        <div className="user-detail__header-info">
          <h2 className="user-detail__name">{fullName}</h2>
          {u.username && (
            <span className="user-detail__handle">@{u.username}</span>
          )}
          <div className="user-detail__badges">
            <span className={roleClass(u.role)}>{roleLabel(u.role)}</span>
            {u.verified && (
              <span className="user-detail__badge user-detail__badge--verified">
                Verified
              </span>
            )}
            {u.premium && (
              <span className="user-detail__badge user-detail__badge--premium">
                Premium
              </span>
            )}
            {u.isBanned && (
              <span className="user-detail__badge user-detail__badge--banned">
                Banned
              </span>
            )}
          </div>
          <div className="user-detail__email">{u.email}</div>
        </div>
      </div>

      <section className="user-detail__card user-detail__card--actions">
        <h3 className="user-detail__card-title">Actions</h3>
        {isSelf ? (
          <p className="user-detail__empty">
            You can't modify your own admin account from here.
          </p>
        ) : (
          <>
            <div className="user-detail__action-row">
              <label className="user-detail__action-label">Role</label>
              <select
                className="user-detail__role-select"
                value={u.role}
                onChange={handleRoleChange}
                disabled={isRoleSaving}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="user-detail__action-row user-detail__action-row--buttons">
              <button
                type="button"
                className={
                  u.isBanned
                    ? 'user-detail__action-btn user-detail__action-btn--unban'
                    : 'user-detail__action-btn user-detail__action-btn--ban'
                }
                onClick={handleBanToggle}
                disabled={isBanSaving}
              >
                {isBanSaving
                  ? 'Saving…'
                  : u.isBanned
                  ? 'Unban user'
                  : 'Ban user'}
              </button>
              <button
                type="button"
                className="user-detail__action-btn user-detail__action-btn--delete"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
            {actionError && (
              <div className="admin-users__error" style={{ marginTop: 12 }}>
                {actionError}
              </div>
            )}
          </>
        )}
      </section>

      <div className="user-detail__grid">
        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Basic</h3>
          <Field label="Bio" value={u.bio} />
          <Field label="Date of birth" value={formatDate(u.dateOfBirth)} />
          <Field label="Gender" value={u.gender} />
          <Field label="Language" value={u.language} />
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Location</h3>
          {hasLocation ? (
            <>
              <Field label="City" value={u.location?.city} />
              <Field label="State / Region" value={u.location?.state} />
              <Field label="Country" value={u.location?.country} />
              {u.location?.coordinates && (
                <Field
                  label="Coordinates"
                  value={
                    <span className="user-detail__coords">
                      {u.location.coordinates.latitude.toFixed(5)},{' '}
                      {u.location.coordinates.longitude.toFixed(5)}
                    </span>
                  }
                />
              )}
            </>
          ) : (
            <p className="user-detail__empty">No location set.</p>
          )}
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Stats</h3>
          {u.stats ? (
            <>
              <Field label="Events created" value={u.stats.eventsCreated} />
              <Field label="Events visited" value={u.stats.eventsVisited} />
              <Field
                label="General rating"
                value={u.stats.generalRating?.toFixed(1) ?? '—'}
              />
            </>
          ) : (
            <p className="user-detail__empty">No stats yet.</p>
          )}
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Auth</h3>
          <Field label="Grant type" value={u.grantType} />
          <Field label="Verified" value={u.verified ? 'Yes' : 'No'} />
          <Field
            label="FCM token"
            value={
              u.fcmToken ? (
                <code className="user-detail__code">
                  {truncateToken(u.fcmToken)}
                </code>
              ) : (
                '—'
              )
            }
          />
          <Field
            label="User ID"
            value={<code className="user-detail__code">{u.id}</code>}
          />
        </section>

        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">Activities</h3>
          {u.activities && u.activities.length > 0 ? (
            <div className="user-detail__tags">
              {u.activities.map((a) => (
                <span key={a.id} className="user-detail__tag">
                  {a.name}
                </span>
              ))}
            </div>
          ) : (
            <p className="user-detail__empty">No activities selected.</p>
          )}
        </section>

        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">Socials</h3>
          {u.socials && u.socials.length > 0 ? (
            <div className="user-detail__socials">
              {u.socials.map((s, i) => (
                <div key={`${s.value}-${i}`} className="user-detail__social">
                  <span className="user-detail__social-network">{s.value}</span>
                  <span className="user-detail__social-link">{s.link}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="user-detail__empty">No socials linked.</p>
          )}
        </section>

        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">Onboarding</h3>
          {u.onboardingState ? (
            <>
              <Field
                label="Tour version"
                value={u.onboardingState.tourVersion}
              />
              <Field
                label="Completed"
                value={u.onboardingState.tourCompleted ? 'Yes' : 'No'}
              />
              <Field
                label="Skipped"
                value={u.onboardingState.tourSkipped ? 'Yes' : 'No'}
              />
              <Field
                label="Current step"
                value={u.onboardingState.tourCurrentStep}
              />
              <Field
                label="Started at"
                value={formatDate(u.onboardingState.tourStartedAt)}
              />
              <Field
                label="Completed at"
                value={formatDate(u.onboardingState.tourCompletedAt)}
              />
            </>
          ) : (
            <p className="user-detail__empty">Onboarding not started.</p>
          )}
        </section>
      </div>
    </div>
  );
};
